import React, { useEffect, useState } from 'react';
import { usePatientContactAvailability } from 'infrastructure/hooks/patient/use-patient-contact-availability';
import { weekDaysDataSource } from 'infrastructure/data-sources/week-days';
import BaseSpinWrapper from 'components/atoms/base-spin-wrapper';
import { Availability } from 'infrastructure/classes/availability';
import { useTranslation } from 'react-i18next';
import { convertDateToTimeZone, DateFormatter } from 'infrastructure/functions';
import { DateFormats } from 'infrastructure/enums';
import classNames from 'classnames';

import s from './styles.module.scss';
import ContactAvailabilityViewDay from './day';

import type { IWeekDayItem } from 'infrastructure/data-sources/week-days';

interface IProps {
  loading?: boolean;
  patientGuid: string;
  timezone: string;
}

const CallDashboardContactAvailabilityForm: React.FC<IProps> = (props) => {
  const { loading: loadingProp = false, patientGuid, timezone } = props;

  const { t } = useTranslation();

  const [time, setTime] = useState(convertDateToTimeZone(new Date(), timezone));

  const { loading: isFetching, availabilities } = usePatientContactAvailability(
    {
      patientGuid,
    },
  );

  const anytime = !availabilities.length;

  const loading = isFetching || loadingProp;

  const weekDays = weekDaysDataSource();

  const isAvailable = Availability.isAvailable(availabilities, timezone);

  const availableText = isAvailable
    ? t('labels.availableNow')
    : t('labels.notAvailableNow');
  const statusText = anytime ? t('labels.anytime') : availableText;

  const availabilityCls = classNames(s.availability, {
    [s.green]: isAvailable,
  });

  const prepareAvailabilities = (day: IWeekDayItem) => {
    const result: Availability[] = [];
    availabilities.forEach((availability) => {
      if (day.index === availability.day) result.push(availability);
    });

    return result;
  };

  useEffect(() => {
    const timer = setInterval(() => {
      setTime(convertDateToTimeZone(new Date(), timezone));
    }, 60000);

    return () => {
      clearInterval(timer);
    };
  }, []);

  return (
    <BaseSpinWrapper spinning={loading}>
      <div className={s.head}>
        <span className={s.title}>Availability</span>
        <span className={availabilityCls}>
          {`${DateFormatter({
            date: time,
            format: DateFormats['h:mm a'],
          })} (${timezone}) – ${statusText}`}
        </span>
      </div>
      <ul className={s.list}>
        {weekDays.map((el) => (
          <ContactAvailabilityViewDay
            key={el.index}
            day={el}
            times={prepareAvailabilities(el)}
            anytime={anytime}
          />
        ))}
      </ul>
    </BaseSpinWrapper>
  );
};

export default CallDashboardContactAvailabilityForm;
